/*
  Cuatro canales. Selector de público, canal activo, líneas al producto,
  rotación automática en celular y revelado a los 20 segundos en escritorio.
*/
if (!customElements.get('four-ways')) {
  customElements.define(
    'four-ways',
    class FourWays extends HTMLElement {
      static STEP = 4500;
      static REVEAL_AFTER = 20;

      connectedCallback() {
        this.stage = this.querySelector('[data-fw-stage]');
        this.svg = this.querySelector('[data-fw-lines]');
        this.tabList = this.querySelector('.fw-tabs');
        this.tabs = [...this.querySelectorAll('.fw-tab')];
        this.cards = [...this.querySelectorAll('.fw-card')];
        this.spots = [...this.querySelectorAll('.fw-spot')];
        this.chips = [...this.querySelectorAll('.fw-chip')];
        if (!this.stage || this.cards.length === 0) return;

        this.desktop = window.matchMedia('(min-width: 990px)');
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.active = this.cards[0].dataset.channel;
        this.audience = 'parents';
        this.pinned = false;
        this.inView = false;
        this.seen = 0;
        this.paths = {};

        this.stage.style.setProperty('--fw-step', FourWays.STEP + 'ms');
        this.bind();
        this.setActive(this.active);
        this.observe();

        this.onResize = () => this.drawLines();
        this.onChange = () => {
          this.drawLines();
          this.schedule();
        };
        this.onVisibility = () => this.schedule();
        window.addEventListener('resize', this.onResize);
        this.desktop.addEventListener('change', this.onChange);
        this.reduced.addEventListener('change', this.onChange);
        document.addEventListener('visibilitychange', this.onVisibility);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.drawLines());

        this.revealTimer = setInterval(() => this.tickReveal(), 1000);
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        clearInterval(this.revealTimer);
        if (this.observer) this.observer.disconnect();
        window.removeEventListener('resize', this.onResize);
        if (this.desktop) this.desktop.removeEventListener('change', this.onChange);
        if (this.reduced) this.reduced.removeEventListener('change', this.onChange);
        document.removeEventListener('visibilitychange', this.onVisibility);
      }

      bind() {
        this.cards.forEach((card) => {
          card.addEventListener('mouseenter', () => {
            if (this.desktop.matches) this.setActive(card.dataset.channel);
          });
          card.addEventListener('focus', () => this.setActive(card.dataset.channel));
          card.addEventListener('click', () => {
            this.pin();
            this.setActive(card.dataset.channel);
          });
        });

        this.spots.forEach((spot) => {
          spot.addEventListener('mouseenter', () => {
            if (this.desktop.matches) this.setActive(spot.dataset.channel);
          });
          spot.addEventListener('click', () => {
            this.pin();
            this.setActive(spot.dataset.channel);
          });
        });

        this.chips.forEach((chip, i) => {
          chip.addEventListener('click', () => {
            this.pin();
            this.setActive(chip.dataset.channel);
          });
          chip.addEventListener('keydown', (event) => {
            const next = this.arrowTarget(event, this.chips, i);
            if (!next) return;
            this.pin();
            next.focus();
            this.setActive(next.dataset.channel);
          });
        });

        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.selectAudience(tab));
          tab.addEventListener('keydown', (event) => {
            const next = this.arrowTarget(event, this.tabs, i);
            if (!next) return;
            next.focus();
            this.selectAudience(next);
          });
        });
      }

      arrowTarget(event, list, i) {
        if (event.key === 'ArrowRight') return list[(i + 1) % list.length];
        if (event.key === 'ArrowLeft') return list[(i + list.length - 1) % list.length];
        return null;
      }

      selectAudience(tab) {
        this.tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
        });
        this.stage.setAttribute('aria-labelledby', tab.id);
        if (this.tabList) this.tabList.classList.remove('is-idle');
        if (this.audience === tab.dataset.audience) return;
        this.audience = tab.dataset.audience;

        const swap = () => {
          this.cards.forEach((card) => {
            card.querySelectorAll('.fw-copy').forEach((copy) => {
              copy.hidden = copy.dataset.audience !== this.audience;
            });
            card.querySelector('.fw-copies')?.classList.remove('is-swapping');
          });
          requestAnimationFrame(() => this.drawLines());
        };

        if (this.reduced.matches) {
          swap();
          return;
        }
        this.cards.forEach((card) => card.querySelector('.fw-copies')?.classList.add('is-swapping'));
        setTimeout(swap, 180);
      }

      setActive(channel) {
        this.active = channel;
        this.cards.forEach((el) => el.classList.toggle('is-on', el.dataset.channel === channel));
        this.spots.forEach((el) => el.classList.toggle('is-on', el.dataset.channel === channel));
        this.chips.forEach((el) => {
          const on = el.dataset.channel === channel;
          el.setAttribute('aria-selected', on ? 'true' : 'false');
          el.tabIndex = on ? 0 : -1;
        });
        Object.keys(this.paths).forEach((key) => this.paths[key].classList.toggle('is-on', key === channel));
      }

      drawLines() {
        this.svg.innerHTML = '';
        this.paths = {};
        if (!this.desktop.matches) return;
        const box = this.stage.getBoundingClientRect();
        this.svg.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`);
        this.cards.forEach((card) => {
          const channel = card.dataset.channel;
          const spot = this.spots.find((s) => s.dataset.channel === channel);
          if (!spot) return;
          const c = card.getBoundingClientRect();
          const s = spot.getBoundingClientRect();
          const sx = s.left + s.width / 2 - box.left;
          const sy = s.top + s.height / 2 - box.top;
          const leftSide = c.left + c.width / 2 < box.left + box.width / 2;
          const cx = (leftSide ? c.right : c.left) - box.left;
          const cy = c.top + Math.min(40, c.height / 2) - box.top;
          const mid = cx + (sx - cx) * 0.45;
          const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path.setAttribute('d', `M${cx} ${cy} H${mid} L${sx} ${sy}`);
          if (channel === this.active) path.classList.add('is-on');
          this.svg.appendChild(path);
          this.paths[channel] = path;
        });
      }

      observe() {
        if (!('IntersectionObserver' in window)) {
          this.inView = true;
          this.drawLines();
          this.schedule();
          return;
        }
        this.observer = new IntersectionObserver(
          (entries) => {
            this.inView = entries[0].isIntersecting;
            if (this.inView) this.drawLines();
            this.schedule();
          },
          { threshold: 0.35 }
        );
        this.observer.observe(this.stage);
      }

      /* Celular: rota los canales hasta que el comprador toca uno; después queda fijo. */
      autoAllowed() {
        return !this.pinned && !this.desktop.matches && !this.reduced.matches && this.chips.length > 1;
      }

      schedule() {
        clearTimeout(this.timer);
        this.stage.classList.toggle('is-auto', this.autoAllowed());
        this.chips.forEach((chip) => chip.classList.remove('is-timing'));
        if (!this.autoAllowed() || !this.inView || document.hidden) return;
        const current = this.chips.find((chip) => chip.dataset.channel === this.active) || this.chips[0];
        void current.offsetWidth;
        current.classList.add('is-timing');
        this.timer = setTimeout(() => {
          const i = this.chips.indexOf(current);
          this.setActive(this.chips[(i + 1) % this.chips.length].dataset.channel);
          this.schedule();
        }, FourWays.STEP);
      }

      pin() {
        if (this.pinned) return;
        this.pinned = true;
        this.schedule();
      }

      /* Escritorio: a los 20 segundos con la sección en pantalla se revelan todos los detalles. */
      tickReveal() {
        if (!this.desktop.matches || !this.inView || document.hidden) return;
        this.seen += 1;
        if (this.seen < FourWays.REVEAL_AFTER) return;
        this.stage.classList.add('is-revealed');
        clearInterval(this.revealTimer);
      }
    }
  );
}
